import { requestLaravel } from "../../../../../utils/api";
import { ApiResponse, IProjectDocument } from "../../../core/_models";
import { EvaluationDetails, EvaluationQuestionsResponse, EvaluatorsResponse, IReferenceDocument, PrincipleChartData, PrinciplesResponse, ProjectDetails, ProjectOverview, UpdateEvaluationScorePayload } from "./_models";

export function getProjectOverviewData(
    project_id: number,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<ProjectOverview>>(
        "/project-evaluation-tool/get-overview-data",
        { project_id },
        { signal }
    );
}

export function getProjectDetails(
    project_id: number,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<ProjectDetails>>(
        "/project-evaluation-tool/get-project-details",
        { project_id },
        { signal }
    );
}

export function getProjectEvaluators(
    project_id: number,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<EvaluatorsResponse>>(
        "/project-evaluation-tool/get-project-evaluators",
        { project_id },
        { signal }
    );
}

export function getPrincipleChartData(
    project_id: number,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<{ principle_chart_data: PrincipleChartData[], principle_colors: Record<string, string>, sub_principle_weights: Record<string, number> }>>(
        "/project-evaluation-tool/get-principle-chart-data",
        { project_id },
        { signal }
    );
}

export function getEvaluationPrinciples(
    project_id: number,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<PrinciplesResponse>>(
        "/project-evaluation-tool/get-evaluation-principles",
        { project_id },
        { signal }
    );
}

export function getEvaluationQuestions(
    project_id: number,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<EvaluationQuestionsResponse>>(
        "/project-evaluation-tool/get-evaluation-questions",
        { project_id },
        { signal }
    );
}


export function getEvaluationResponses(
    project_id: number,
    evaluator_user_id: number,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<EvaluationDetails>>(
        "/project-evaluation-tool/get-evaluator-responses",
        { project_id, evaluator_user_id },
        { signal }
    );
}


export function updateEvaluationScore(
    payload: UpdateEvaluationScorePayload,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse>(
        "/project-evaluation-tool/update-question-scores",
        payload,
        { signal }
    );
}
export function completeProjectEvaluation(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse>(
        "/project-evaluation-tool/complete-evaluation",
        { project_id },
        { signal }
    );
}

export function getPrincipleReferenceDocuments(project_id: number, pp_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ docs: IReferenceDocument[] }>>(
        "/project-evaluation-tool/get-principle-reference-documents",
        { project_id, pp_id },
        { signal }
    );
}

export function getProjectPETDocuments(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ project_docs: IProjectDocument[] }>>(
        "/project-evaluation-tool/get-project-reference-documents",
        { project_id },
        { signal }
    );
}

export function uploadProjectPETDocuments(
    payload: { project_id: number; documents: IProjectDocument[] },
    signal?: AbortSignal
) {
    const documents = payload.documents.filter(
        (document: IProjectDocument) => document?.doc_id === 0
    );

    return requestLaravel<ApiResponse<{ project_docs: IProjectDocument[] }>>(
        "/project-evaluation-tool/upload-project-reference-documents",
        { project_id: payload.project_id, documents },
        { signal }
    );
}

export function deleteProjectPETDocument(
    payload: { project_id: number; doc_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<{
        doc_id: number;
        deleted: boolean;
        project_docs: IProjectDocument[];
    }>>(
        "/project-evaluation-tool/delete-project-reference-document",
        payload,
        { signal }
    );
}

export function saveAdditionalNotes(project_id: number, notes: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse>(
        "/project-evaluation-tool/save-additional-notes",
        { project_id, eval_additional_notes: notes },
        { signal }
    );
}

export function getAdditionalNotes(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ eval_additional_notes: string }>>(
        "/project-evaluation-tool/get-additional-notes",
        { project_id },
        { signal }
    );
}

export function getPanopticScoringKeywords(signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ keyword: string, score: number, thinking: string }[]>>(
        "/project-evaluation-tool/get-panoptic-scoring-keywords",
        { signal }
    );
}

export function getLikertScaleLabels(signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ label: string, score: number }[]>>(
        "/project-evaluation-tool/get-likert-scale-labels",
        { signal }
    );
}